import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { dismissBooking } from '@/services/scheduledBookingService';
import type { Employee, TimesheetEntry } from '@/types/timesheet';
import type { DayName } from '../timesheetGridUtils';

const EMPTY_HOURS = {
  monday: 0,
  tuesday: 0,
  wednesday: 0,
  thursday: 0,
  friday: 0,
  saturday: 0,
  sunday: 0,
};

export interface UseTimesheetRowActionsParams {
  entries: TimesheetEntry[];
  setEntries: React.Dispatch<React.SetStateAction<TimesheetEntry[]>>;
  /** The employee whose timesheet is being edited (may differ from the logged-in user). */
  targetEmployee: Employee | null;
  canDeleteTimesheet: () => boolean;
  tableScrollRef: React.RefObject<HTMLDivElement | null>;
}

export interface UseTimesheetRowActionsResult {
  addRow: () => void;
  duplicateRow: (index: number) => void;
  deleteRow: (index: number) => void;
  confirmDelete: () => void;
  deleteConfirmation: { open: boolean; index: number | null };
  setDeleteConfirmation: React.Dispatch<React.SetStateAction<{ open: boolean; index: number | null }>>;
  updateEntry: (index: number, field: keyof TimesheetEntry, value: unknown) => void;
  updateHours: (index: number, day: DayName, value: string) => void;
  handleDismissBooking: (index: number, reason: string, customReason?: string) => void;
}

/** Row-level grid actions: add, duplicate, delete, edit and booking dismissal. */
export function useTimesheetRowActions({
  entries,
  setEntries,
  targetEmployee,
  canDeleteTimesheet,
  tableScrollRef,
}: UseTimesheetRowActionsParams): UseTimesheetRowActionsResult {
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ open: boolean; index: number | null }>({
    open: false,
    index: null,
  });

  const addRow = () => {
    const newEntry: TimesheetEntry = {
      id: -Date.now(),
      projectId: '',
      costCentreId: '',
      roleId: targetEmployee?.roleId, // Default from the timesheet owner's role, can be changed
      roleName: targetEmployee?.roleId && targetEmployee?.role ? targetEmployee.role : undefined, // Denormalized role name for display
      description: '',
      hours: { ...EMPTY_HOURS },
    };
    setEntries(prevEntries => [...prevEntries, newEntry]);
  };

  // Scroll table to bottom when a new row is added
  const prevEntriesLengthRef = useRef<number>(0);
  useEffect(() => {
    if (entries.length > prevEntriesLengthRef.current && tableScrollRef.current) {
      tableScrollRef.current.scrollTop = tableScrollRef.current.scrollHeight;
    }
    prevEntriesLengthRef.current = entries.length;
  }, [entries.length, tableScrollRef]);

  const deleteRow = (index: number) => {
    // Show confirmation dialog
    setDeleteConfirmation({ open: true, index });
  };

  const confirmDelete = () => {
    if (!canDeleteTimesheet()) {
      toast.error('You do not have permission to delete timesheet lines');
      setDeleteConfirmation({ open: false, index: null });
      return;
    }
    if (deleteConfirmation.index !== null) {
      setEntries(prevEntries => prevEntries.filter((_, i) => i !== deleteConfirmation.index));
      setDeleteConfirmation({ open: false, index: null });
    }
  };

  const duplicateRow = (index: number) => {
    const entry = entries[index];
    const newEntry: TimesheetEntry = {
      ...entry,
      id: -Date.now(),
      hours: { ...EMPTY_HOURS },
      // Remove scheduled booking flags for duplicated entries
      isFromSchedule: false,
      scheduledBookingId: undefined,
    };
    setEntries(prevEntries => [...prevEntries.slice(0, index + 1), newEntry, ...prevEntries.slice(index + 1)]);

    // Focus on the new row after a short delay to allow DOM to update
    setTimeout(() => {
      // Find the first input in the new row (project selector)
      const newRowIndex = index + 1;
      const projectInput = document.querySelector(`[data-row-index="${newRowIndex}"] input`);
      if (projectInput instanceof HTMLElement) {
        projectInput.focus();
      }
    }, 100);
  };

  const updateEntry = (index: number, field: keyof TimesheetEntry, value: unknown) => {
    setEntries(prevEntries => {
      if (index < 0 || index >= prevEntries.length) {
        console.error('❌ Invalid index:', index, 'Total entries:', prevEntries.length);
        toast.error('Error updating entry: Invalid row index');
        return prevEntries;
      }

      const updated = [...prevEntries];
      updated[index] = { ...updated[index], [field]: value };

      return updated;
    });
  };

  const updateHours = (index: number, day: DayName, value: string) => {
    const hours = value === '' ? 0 : parseFloat(value);
    const final = isNaN(hours) ? 0 : hours;
    setEntries(prevEntries => {
      const updated = [...prevEntries];
      updated[index] = {
        ...updated[index],
        hours: { ...updated[index].hours, [day]: final }
      };
      return updated;
    });
  };

  const handleDismissBooking = (index: number, reason: string, customReason?: string) => {
    setEntries(prevEntries => {
      const entry = prevEntries[index];
      const dismissedEntry = dismissBooking(entry, customReason || reason);
      const updated = [...prevEntries];
      updated[index] = dismissedEntry;
      return updated;
    });
  };

  return {
    addRow,
    duplicateRow,
    deleteRow,
    confirmDelete,
    deleteConfirmation,
    setDeleteConfirmation,
    updateEntry,
    updateHours,
    handleDismissBooking,
  };
}
